// Copyright (c) StrangeBee 2023

import { useState } from 'react';
import { AttachmentCaseType, AttachmentType, CaseReportAttachmentOutput } from 'thehive-sdk';
import { openMessage, useLocalMessage } from '@Helpers/';
import { THPreviewType } from './THPreview';
import client from '@Client';

export const supportedAttachmentTypes: Record<string, string[]> = {
    text: [
        'text/html',
        'text/plain',
        'application/json',
        'application/ld+json',
        'application/rtf',
        'application/xml',
        'text/xml',
        'text/css',
        'text/html',
        'text/javascript',
        'application/xhtml+xml',
        'text/rtf',
        'application/octet-stream',
        'text/x-python',
        'text/x-perl',
        'text/x-ruby',
        'text/x-shellscript',
        'text/x-php',
        'text/x-java',
        'text/x-c',
        'text/x-c++',
        'text/x-csharp',
        'text/x-objective-c',
        'text/x-swift',
        'text/x-go',
        'text/x-rust',
        'text/x-haskell',
        'text/x-lua',
        'text/x-scala',
        'text/x-kotlin',
        'text/x-vbnet',
        'text/x-pascal',
        'text/x-tcl',
        'text/x-matlab',
        'text/x-groovy',
        'text/x-dart',
        'text/x-clojure',
        'text/x-elixir',
        'text/x-crystal',
        'text/x-erlang',
        'text/x-lisp',
        'text/x-ocaml',
        'text/x-prolog',
        'text/x-scheme',
        'text/x-yaml',
        'text/x-plaintext',
        'text/x-sh',
        'text/x-python-script',
        'text/x-perl-script',
        'text/x-ruby-script',
        'text/x-shellscript',
        'text/x-php-script',
        'text/x-javascript-script',
        'text/x-lua-script',
        'text/x-tcl-script',
        'text/x-vbscript',
        'text/x-applescript',
        'text/x-powershell-script',
    ],
    csv: ['text/csv'],
    pdf: ['application/pdf'],
    image: [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp',
        'image/svg+xml',
        'image/x-icon',
        'image/vnd.microsoft.icon',
    ],
};

export type GenericAttachmentType = AttachmentCaseType | AttachmentType | CaseReportAttachmentOutput;

export type AttachmentParentType = {
    type: 'case' | 'log' | 'caseReport' | 'alert';
    id: string;
};

type State = {
    content: string | null;
    extension: string | null;
    type: THPreviewType | null;
};

const initialState: State = {
    content: null,
    extension: null,
    type: null,
};

type AttachmentConfig = {
    url: string;
    fileType: THPreviewType;
    extension: string;
} | null;

const checkIfFileIsMarkdown = (fileExtension: string) => {
    const markdownExtensions = ['markdown', 'mdown', 'mkdn', 'md', 'mkd', 'mdwn', 'mdtext'];
    const fileExt = fileExtension.split('.').slice(-1)[0];

    return markdownExtensions.includes(fileExt);
};
const getPreviewConfig = (type: string, extension: string, url: string, name: string): AttachmentConfig => {
    if (type === 'image') {
        return { url, fileType: type, extension };
    } else if (type === 'text' || type === 'application') {
        if (extension === 'pdf' || extension === 'html') {
            return { url, fileType: extension, extension };
        } else if (checkIfFileIsMarkdown(name)) {
            return { url, fileType: 'markdown', extension };
        } else {
            return { url, fileType: 'text', extension };
        }
    }
    return null;
};

export type usePreviewOutput = {
    allowPreview: (contentType: string) => boolean;
    togglePreview: (attachment: GenericAttachmentType, parent?: AttachmentParentType) => void;
    content: string | null;
    type: THPreviewType | null;
    extension: string | null;
};

export const usePreview = (): usePreviewOutput => {
    const [{ content, extension, type }, setPreviewState] = useState<State>(initialState);

    const i = useLocalMessage();

    const sendError = () => openMessage('error', i('attachment.action.preview.error'));

    const getAttachment = async (config: AttachmentConfig) => {
        if (!!config) {
            const { url, fileType, extension } = config;

            try {
                const request = await fetch(url);

                let data: any;
                let type: THPreviewType;

                switch (fileType) {
                    case 'image':
                    case 'pdf':
                    case 'html':
                        type = fileType === 'html' ? 'text' : fileType;
                        setPreviewState((prevState) => ({ ...prevState, type, content: url }));
                        break;

                    case 'markdown':
                    case 'text':
                        data = await request.text();
                        type = fileType === 'markdown' ? fileType : extension === 'csv' ? extension : 'text';

                        setPreviewState((prevState) => ({ ...prevState, type, content: data }));
                        break;
                }
            } catch (e) {
                sendError();
            }
        }
    };

    const togglePreview = (attachment: GenericAttachmentType, parent?: AttachmentParentType) => {
        if (!!attachment) {
            const { contentType, _id } = attachment;
            const [type, extension] = contentType.split('/');

            let url: string;
            let config: AttachmentConfig = null;

            setPreviewState((prevState) => ({ ...prevState, extension }));

            if ('name' in attachment) {
                const { name } = attachment;
                url = `${window.location.origin}${client.apiUrl}/v1/${
                    !!parent ? parent.type + '/' + parent.id + '/' : ''
                }attachment/${_id}`;

                config = getPreviewConfig(type, extension, url, name);
            } else if ('filename' in attachment) {
                url = `${window.location.origin}${client.apiUrl}/v1/caseReport/${_id}/view`;
                config = getPreviewConfig(type, extension, url, attachment.filename);
            }

            getAttachment(config);
        } else {
            setPreviewState(initialState);
        }
    };

    const allowPreview = (contentType: string) =>
        !!Object.keys(supportedAttachmentTypes).find((key) => supportedAttachmentTypes[key].includes(contentType));

    return { allowPreview, togglePreview, content, type, extension };
};
